import { Typography, TypographyProps } from "@mui/material";
import { StandardCSSProperties } from "@mui/system";
import { useLevel } from "react-headings";
import { Property } from "csstype";
import {
  childrenToString,
  useGetTextLang,
} from "foundations/utils/useGetTextLang";

type HeadingColor = "normal" | "light" | "primary" | "neutralDark";

interface BaseHeadingProps extends Pick<
  TypographyProps,
  "children" | "id" | "gutterBottom" | "lang"
> {
  /** @default "normal" */
  color?: HeadingColor;
  variant?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "subtitle1" | "subtitle2";
  /** This allows the heading to be visibly gone, but still accessible to screen readers. */
  hidden?: boolean;
  fontWeight?: Property.FontWeight;
  fontFamily?: Property.FontFamily;
  textAlign?: Property.TextAlign;
  italic?: boolean;
  mx?: StandardCSSProperties["marginLeft"];
  my?: StandardCSSProperties["marginTop"];
  mr?: StandardCSSProperties["marginRight"];
  ml?: StandardCSSProperties["marginLeft"];
  mb?: StandardCSSProperties["marginBottom"];
  mt?: StandardCSSProperties["marginTop"];
  /**
   * Hide from screen readers and don't use a semantically-appropriate heading tag. Use only in
   * cases where visual heading text is needed, and it is contained elsewhere in the accessibility
   * tree.
   */
  ariaHidden?: boolean;
}

interface VisibleHeadingProps extends BaseHeadingProps {
  hidden?: false;
  variant: NonNullable<BaseHeadingProps["variant"]>;
}

interface HiddenHeadingProps extends BaseHeadingProps {
  hidden: true;
  variant?: undefined;
  ariaHidden?: false;
}

type HeadingProps = VisibleHeadingProps | HiddenHeadingProps;

const COLORS: Record<HeadingColor, string> = {
  normal: "text.primary",
  light: "text.secondary",
  primary: "primary",
  neutralDark: "text.dark",
};

/**
 * Generates a heading element with the heading level automatically determined.
 */
export function Heading(props: HeadingProps) {
  const {
    color = "normal",
    variant,
    hidden,
    ariaHidden,
    ...typographyProps
  } = props;

  const getTextLang = useGetTextLang();
  const { Component: headingTag } = useLevel();

  return (
    <Typography
      {...typographyProps}
      lang={getTextLang(childrenToString(props.children))}
      component={ariaHidden ? "span" : headingTag}
      aria-hidden={ariaHidden ? "true" : undefined}
      variant={variant}
      color={COLORS[color]}
      sx={
        hidden
          ? {
              // Following best practices for hiding a heading but keeping it accessible
              // https://wdn.unl.edu/page-title-h1-best-practices#:~:text=You%20can%20visually%20hide%20the,available%20to%20AT%20and%20bots.
              fontSize: "0px",
              width: "1px",
              height: "1px",
              display: "inline-block",
              overflow: "hidden",
              position: "absolute!important",
              border: "0px !important",
              padding: "0px !important",
              margin: "0px!important",
            }
          : {
              textAlign: props.textAlign,
              fontWeight: props.fontWeight,
              fontFamily: props.fontFamily,
              mx: props.mx,
              my: props.my,
              mr: props.mr,
              ml: props.ml,
              mb: props.mb,
              mt: props.mt,
              fontStyle: props.italic ? "italic" : undefined,
            }
      }
    >
      {props.children}
    </Typography>
  );
}
